// =============================================================================
// Brand CTA / button style (Task #1233)
//
// A brand can define an explicit CTA/button style — fill color, optional text
// color, and corner shape — on its design system (`brandDesignSystem.ctaStyle`).
// This module is the ONE shared resolver every consumer uses so LP Express,
// Email Express, and the Ads Express prompt directive stay in lockstep:
//
//   resolveBrandCtaStyle(designSystem)  → BrandCtaStyleSelection | null
//
// HARD GATE: the resolver returns
// null whenever `ctaStyle.enabled` is false, the design system is absent, or
// the fill color is not a valid hex. Every consumer treats null as "do exactly
// what you did before" so an unset brand renders byte-for-byte identically to
// today across all three tools.
// =============================================================================

import {
  isUsableBrandFont,
  isAdSteerableBrandFont,
  selectBrandButtonFont,
  brandFontFamilyName,
  brandFontCssStack,
  brandFontFormat,
  googleCategoryStyleDescription,
} from "./brand-fonts";
import { GOOGLE_FONT_CATALOG } from "./google-fonts";
import type { BrandFont } from "./schema";

// ── Surface scope ────────────────────────────────────────────────────────────
// Which tools consume the brand CTA style. Per product decision (July 2026)
// the style is LIVE FOR ADS ONLY for now — LP Express seeding and the Email
// Express button override are wired but switched off until a follow-up task
// re-enables them. Flip the flag to bring a surface back; the call sites in
// server/routes.ts (LP theme seed) and shared/email-brand-style.ts (button
// override) gate on these and behave byte-identically to a null-CTA brand
// while disabled.
export const BRAND_CTA_SURFACES = {
  ads: true,
  lp: false,
  email: false,
} as const;

export type BrandCtaSurface = keyof typeof BRAND_CTA_SURFACES;

// Surface-gated resolver — the ONE entry point non-ads consumers call so a
// disabled surface cannot accidentally consume the CTA style. Returns null
// (the "do exactly what you did before" value) whenever the surface is off,
// regardless of the brand's ctaStyle.
export function resolveBrandCtaStyleForSurface(
  surface: BrandCtaSurface,
  designSystem: any,
): BrandCtaStyleSelection | null {
  if (!BRAND_CTA_SURFACES[surface]) return null;
  return resolveBrandCtaStyle(designSystem);
}

export const BRAND_CTA_SHAPES = ["square", "rounded", "pill"] as const;
export type BrandCtaShape = (typeof BRAND_CTA_SHAPES)[number];

export const BRAND_CTA_FILLS = ["solid", "outline"] as const;
export type BrandCtaFill = (typeof BRAND_CTA_FILLS)[number];

// A resolved CTA button-label font: one of the brand's saved fonts (Design
// System → Fonts), pre-digested so no consumer re-reads the fonts list.
// `styleDescription` (Google category phrase) exists only for ad-steerable
// (Google) fonts — uploads never steer ads.
export interface BrandCtaFontSelection {
  fontId: string;
  family: string; // human-facing family ("Lato") — used for Google loads + ads
  cssFamily: string; // concrete CSS family name (upload → "brand-upload-<id>")
  cssStack: string; // full CSS font-family stack with fallback tail
  source: "google" | "upload";
  fileUrl: string | null; // upload R2 URL (null for Google)
  fileFormat: string | null; // upload @font-face format hint (null for Google)
  styleDescription: string | null; // ads-directive phrase (Google only)
}

// A resolved, snapshot-stable CTA style. `textColor` is ALWAYS concrete here —
// when the brand didn't pick one it's auto-derived (`textColorSource: "auto"`):
// solid → white/near-black by fill luminance, outline → the fill color itself
// (the label sits on the page background, not the fill) — so consumers never
// re-implement contrast logic.
export interface BrandCtaStyleSelection {
  fillColor: string; // normalized #rrggbb
  textColor: string; // normalized #rrggbb (explicit or auto-contrast)
  textColorSource: "explicit" | "auto";
  // null = brand didn't pick a shape → each surface keeps its own default radius.
  shape: BrandCtaShape | null;
  // "outline" = transparent button with a border in fillColor. Old persisted
  // snapshots predate this field — every consumer treats undefined as "solid".
  fillStyle: BrandCtaFill;
  // Uppercase the button label. Old snapshots: undefined ⇒ false.
  allCaps: boolean;
  // Button-label font, or null when the brand didn't pick one (or the saved
  // font was deleted/unusable). Old snapshots: undefined ⇒ null.
  font: BrandCtaFontSelection | null;
}

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Normalize a color string to lowercase #rrggbb, or null when invalid. Accepts
// 3- and 6-digit hex, with or without the leading "#". We never store or emit
// arbitrary CSS color expressions.
export function normalizeCtaHex(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const m = v.trim().match(HEX_RE);
  if (!m) return null;
  let h = m[1].toLowerCase();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return `#${h}`;
}

// WCAG relative luminance (0 = black, 1 = white) of a normalized hex.
export function ctaRelLuminance(hex: string): number {
  const h = hex.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const s = parseInt(h.slice(i, i + 2), 16) / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Automatic contrast text color for a button fill: near-black on light fills,
// white on dark fills (mirrors the existing satori hero-lockup convention).
export function autoCtaTextColor(fillHex: string): string {
  return ctaRelLuminance(fillHex) > 0.55 ? "#1a1a1a" : "#ffffff";
}

// Resolve the brand's CTA-label font from the design system's saved fonts
// list. An explicit ctaStyle.fontId always wins; when none is picked, the
// saved font carrying the "Buttons" role (selectBrandButtonFont) fills in.
// Null whenever neither exists, the id no longer maps to a saved font, or the
// resolved font is unusable (upload without a stored file URL).
function resolveCtaFont(
  designSystem: any,
  fontId: unknown,
): BrandCtaFontSelection | null {
  const fonts: BrandFont[] = Array.isArray(designSystem?.fonts)
    ? designSystem.fonts
    : [];
  const explicit =
    typeof fontId === "string" && fontId
      ? fonts.find((f) => f?.id === fontId) ?? null
      : null;
  const font = explicit ?? selectBrandButtonFont(fonts);
  if (!font || !isUsableBrandFont(font)) return null;
  const catalogEntry =
    font.source === "google"
      ? GOOGLE_FONT_CATALOG.find((g) => g.family === font.family) ?? null
      : null;
  return {
    fontId: font.id,
    family: font.family,
    cssFamily: brandFontFamilyName(font),
    cssStack: brandFontCssStack(font),
    source: font.source === "upload" ? "upload" : "google",
    fileUrl: font.source === "upload" ? font.url ?? null : null,
    fileFormat: font.source === "upload" ? brandFontFormat(font) : null,
    styleDescription: isAdSteerableBrandFont(font)
      ? googleCategoryStyleDescription(catalogEntry?.category ?? null)
      : null,
  };
}

// CSS border-radius for a brand CTA shape. Matches the LP theme + email
// conventions already in the codebase (square 0 / rounded 8px / pill 9999px).
export function ctaShapeRadiusCss(shape: BrandCtaShape): string {
  if (shape === "pill") return "9999px";
  if (shape === "square") return "0px";
  return "8px";
}

// Server/client-authoritative resolution of the brand CTA style. Accepts the
// RAW design-system blob (already-zod-parsed or straight off the brand row) and
// validates the fields it needs itself, so a legacy/corrupt blob degrades to
// null (legacy render), never throws.
export function resolveBrandCtaStyle(
  designSystem: any,
): BrandCtaStyleSelection | null {
  const cta = designSystem?.ctaStyle;
  if (!cta || typeof cta !== "object" || cta.enabled !== true) return null;
  const fillColor = normalizeCtaHex(cta.fillColor);
  if (!fillColor) return null;
  const explicitText = normalizeCtaHex(cta.textColor);
  const shape = BRAND_CTA_SHAPES.includes(cta.shape) ? (cta.shape as BrandCtaShape) : null;
  // Legacy blobs have no `fill` field ⇒ solid (today's behavior exactly).
  const fillStyle: BrandCtaFill = cta.fill === "outline" ? "outline" : "solid";
  // Outline auto text = the fill color itself: the label sits on the page
  // background, so luminance-vs-fill contrast logic doesn't apply.
  const autoText =
    fillStyle === "outline" ? fillColor : autoCtaTextColor(fillColor);
  return {
    fillColor,
    textColor: explicitText ?? autoText,
    textColorSource: explicitText ? "explicit" : "auto",
    shape,
    fillStyle,
    allCaps: cta.allCaps === true,
    font: resolveCtaFont(designSystem, cta.fontId),
  };
}

// ── LP Express theme seed ────────────────────────────────────────────────────

// Convert a resolved brand CTA style into an LP Express page-theme seed
// (LpTheme fields). Applied ONCE at page GENERATION and persisted on the page,
// so later brand edits never retroactively restyle existing or published pages,
// and per-page theme edits trivially win (they edit the same persisted theme).
// `ctaTextColor` is seeded only when the brand picked one explicitly — an auto
// text color stays unset so the LP surface keeps its own luminance-based
// auto-contrast fallback. Returns null when the brand has no CTA style, in
// which case pages must be created exactly as today (no theme seeded).
// `buttonFill` is seeded only for "outline" (solid leaves the page's default
// fill untouched, exactly as before this option existed). `ctaFontFamily` uses
// the SAME grammar as per-block LpElementStyle.fontFamily: a Google catalog
// family name for saved Google fonts (survives later brand-font deletion) or a
// "brand:<id>" token for uploads (resolved against the brand's fonts at render).
export interface BrandCtaLpThemeSeed {
  ctaColor: string;
  ctaTextColor?: string;
  buttonShape?: BrandCtaShape;
  buttonFill?: "outline";
  ctaAllCaps?: boolean;
  ctaFontFamily?: string;
}

export function brandCtaToLpThemeSeed(
  selection: BrandCtaStyleSelection | null | undefined,
): BrandCtaLpThemeSeed | null {
  if (!selection || !selection.fillColor) return null;
  const seed: BrandCtaLpThemeSeed = {
    ctaColor: selection.fillColor,
  };
  if (selection.textColorSource === "explicit") seed.ctaTextColor = selection.textColor;
  if (selection.shape) seed.buttonShape = selection.shape;
  if (selection.fillStyle === "outline") seed.buttonFill = "outline";
  if (selection.allCaps === true) seed.ctaAllCaps = true;
  if (selection.font) {
    seed.ctaFontFamily =
      selection.font.source === "upload"
        ? `brand:${selection.font.fontId}`
        : selection.font.family;
  }
  return seed;
}

// ── Natural-language description (Ads Express prompt directive) ─────────────

// Coarse human color name for a hex — enough for an image model to aim at the
// right family alongside the exact hex value. Image models anchor on WORDS far
// more than hex codes (a real render turned "#EC5FE8 pink" into coral-red), so
// the vocabulary is deliberately finer than a toy bucket list: magenta is
// split from pink, and strongly saturated mid-tone colors get a "vivid"
// qualifier so the model doesn't drift toward a muted neighbor family.
export function describeCtaHexColor(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lum = ctaRelLuminance(hex);
  if (max - min < 24) {
    if (lum > 0.85) return "white";
    if (lum > 0.5) return "light gray";
    if (lum > 0.08) return "dark gray";
    return "black";
  }
  // Hue in degrees.
  const d = max - min;
  let hue = 0;
  if (max === r) hue = ((g - b) / d) % 6;
  else if (max === g) hue = (b - r) / d + 2;
  else hue = (r - g) / d + 4;
  hue = (hue * 60 + 360) % 360;
  let name: string;
  if (hue < 15 || hue >= 350) name = "red";
  else if (hue < 40) name = "orange";
  else if (hue < 70) name = "yellow";
  else if (hue < 160) name = "green";
  else if (hue < 200) name = "teal";
  else if (hue < 250) name = "blue";
  else if (hue < 290) name = "purple";
  else if (hue < 335) name = "magenta pink";
  else name = "pink";
  if (lum <= 0.12) return `deep ${name}`;
  if (lum >= 0.6) return `light ${name}`;
  // Strong chroma at mid luminance = an unmistakably saturated color; say so.
  if (d >= 100) return `vivid ${name}`;
  return name;
}

function describeCtaShape(shape: BrandCtaShape): string {
  if (shape === "pill") return "a fully rounded pill shape (semicircular ends)";
  if (shape === "square") return "sharp square corners with no rounding";
  return "softly rounded corners";
}

// Compose the STRICTLY-ADDITIVE CTA-button style directive onto a base prompt.
// Returns
// `basePrompt` UNCHANGED when there is no selection so the unset path is
// byte-for-byte identical to today. Computed from the base each render so a
// resume re-applies it idempotently rather than double-stacking.
export function composeCtaStyleDirective(
  basePrompt: string,
  selection: BrandCtaStyleSelection | null | undefined,
): string {
  if (!selection || !selection.fillColor) return basePrompt;
  const fillDesc = describeCtaHexColor(selection.fillColor);
  // Old persisted snapshots predate fillStyle/allCaps/font — undefined must
  // degrade to the exact solid phrasing those snapshots were rendered with.
  const outline = selection.fillStyle === "outline";
  const parts: string[] = [
    outline
      ? `render it as an outline-style button: a transparent interior with a crisp ${fillDesc} border (border color ${selection.fillColor.toUpperCase()})`
      : `render it as a solid ${fillDesc} button (fill color ${selection.fillColor.toUpperCase()})`,
    `with ${describeCtaHexColor(selection.textColor)} button label text (${selection.textColor.toUpperCase()})`,
  ];
  if (selection.allCaps === true)
    parts.push(`with the button label written in ALL CAPITAL LETTERS`);
  const ctaFont = selection.font;
  if (ctaFont && ctaFont.source === "google" && ctaFont.family) {
    const fontStyle = ctaFont.styleDescription
      ? ` (${ctaFont.styleDescription})`
      : "";
    parts.push(
      `with the button label drawn in a typeface closely resembling "${ctaFont.family}"${fontStyle}`,
    );
  }
  if (selection.shape) parts.push(`and ${describeCtaShape(selection.shape)}`);
  return `${basePrompt}\n\nBRAND CTA BUTTON STYLE: If this ad includes a CTA button (e.g. "Shop Now", "Learn More", or any clickable-looking button element), ${parts.join(", ")}. This brand button direction OVERRIDES any other button color or shape described earlier in this prompt AND any button styling visible in the reference image(s) — keep the button's position, size, and label text exactly as directed, but draw the button itself in this brand style. Keep the label fully legible. This applies ONLY to CTA/button elements — do not recolor headlines, badges, backgrounds, the product, or its packaging.`;
}

// ── Sim-side data stanza (Task #1529, `brand_cta_style` beta flag) ───────────
//
// Mirrors the brand color palette wiring (shared/brand-color-adherence.ts,
// Task #1495): the CTA style reaches Ads Express renders VIA SIM only. A
// snapshot-carrying standard non-meme run appends the DATA-ONLY
// "BRAND BUTTON STYLE — …" stanza below to the Sim payload's brandInfo field
// AND routes to the beta workflow pair — the button-styling RULES live in the
// USER's Sim prompts, and ONLY in that pair, keyed off this stanza's label
// (stanza and rules always travel together, VARIANT MODE precedent). The
// stanza's label and "Fill: color word (#HEX), solid|outline; …" shape are a
// CONTRACT with those Sim prompts — do not rewording-drift them without
// updating the workflows. Nothing here touches the Fal render prompt (the old
// composeCtaStyleDirective append is deliberately disconnected — user
// decision), and there are no QA gates or re-rolls.

// The SINGLE runtime gate for the CTA-style stanza + beta-pair routing (same
// pattern as brandColorPaletteActive). True only for a non-meme ad unit whose
// run carries a snapshotted selection with a fill color. Meme ad units share
// the standard ctPayload chain but are explicitly OUT of scope: a granted
// meme run's payload and workflow routing stay byte-for-byte identical.
export function brandCtaStyleActive(args: {
  selection: BrandCtaStyleSelection | null | undefined;
  isMemeAdUnit: boolean;
}): boolean {
  if (args.isMemeAdUnit) return false;
  const s = args.selection;
  if (!s || typeof s !== "object") return false;
  return typeof s.fillColor === "string" && !!normalizeCtaHex(s.fillColor);
}

// Compose the DATA-ONLY brand button style stanza appended to the Sim
// payload's brandInfo field. No rules here — the styling rules live in the
// user's Sim prompts (beta workflow pair only), keyed off this stanza's
// label. Reuses describeCtaHexColor so color naming matches the palette
// stanza. Legacy snapshot fields degrade the same way the old render
// directive did: missing fillStyle ⇒ solid, missing allCaps ⇒ no, shape only
// when picked, font only for ad-steerable (Google) fonts with a family.
// Returns "" for a null/invalid selection so the unset path stays
// byte-for-byte identical to today.
export function composeBrandCtaStyleStanza(
  selection: BrandCtaStyleSelection | null | undefined,
): string {
  if (!selection || typeof selection !== "object") return "";
  const fill = normalizeCtaHex(selection.fillColor);
  if (!fill) return "";
  const fillStyle = selection.fillStyle === "outline" ? "outline" : "solid";
  const parts: string[] = [
    `Fill: ${describeCtaHexColor(fill)} (${fill.toUpperCase()}), ${fillStyle}`,
  ];
  const text = normalizeCtaHex(selection.textColor);
  if (text) parts.push(`Text: ${describeCtaHexColor(text)} (${text.toUpperCase()})`);
  if (selection.shape && BRAND_CTA_SHAPES.includes(selection.shape)) {
    parts.push(`Shape: ${selection.shape}`);
  }
  parts.push(`Caps: ${selection.allCaps === true ? "yes" : "no"}`);
  const font = selection.font;
  if (font && font.source === "google" && font.family) {
    parts.push(
      `Font: ${font.family}${font.styleDescription ? ` (${font.styleDescription})` : ""}`,
    );
  }
  return `BRAND BUTTON STYLE — ${parts.join("; ")}.`;
}

// Escalated CTA-style directive for the QA re-roll path: when the vision QA
// gate flags a render whose CTA button ignored the brand color, the re-roll
// PREPENDS this to the prompt — models weight the prompt opening most, and the
// appended directive already lost once. Empty string when there is no
// selection so callers can gate on truthiness.
export function composeCtaStyleRerollEscalation(
  selection: BrandCtaStyleSelection | null | undefined,
): string {
  if (!selection || !selection.fillColor) return "";
  const fillDesc = describeCtaHexColor(selection.fillColor);
  const fillHex = selection.fillColor.toUpperCase();
  const textDesc = describeCtaHexColor(selection.textColor);
  const textHex = selection.textColor.toUpperCase();
  const outline = selection.fillStyle === "outline";
  const fillClause = outline
    ? `a transparent interior with a crisp ${fillDesc} border (border color EXACTLY ${fillHex})`
    : `a solid ${fillDesc} fill (fill color EXACTLY ${fillHex} — that exact ${fillDesc} shade, not any neighboring color)`;
  return `CRITICAL BRAND BUTTON REQUIREMENT — READ FIRST: Every CTA button in this image MUST be drawn with ${fillClause} and ${textDesc} label text (${textHex}). A previous attempt at this exact image FAILED because the button was painted in a different color instead — do not repeat that mistake. This requirement outranks EVERY button color or style instruction that appears later in this prompt AND any button styling visible in the reference image(s). Keep the button's position, size, shape, and label text exactly as directed below; only the button's colors follow this rule. This applies ONLY to CTA/button elements — never recolor headlines, badges, backgrounds, the product, or its packaging.`;
}
